'use client';

import { CSSProperties, ReactNode, useEffect, useRef, useState } from 'react';

import { useInView } from '@/hooks/use-in-view';

type Props = {
  quote: string;
  /** Root (the tall scroll track) and sticky frame (`<figure>`) classes. */
  className?: string;
  stickyClassName?: string;
  quoteClassName?: string;
  /** Server-rendered parts around the quote, laid out by the parent's grid. */
  openMark: ReactNode;
  closeMark: ReactNode;
  author: ReactNode;
};

// Colour band behind the reading head: 0 = not read yet (grey) → wasabi → olive → 1 = read (ink).
const STOPS: [number, [number, number, number]][] = [
  [0, [214, 213, 219]],
  [0.12, [214, 232, 140]],
  [0.32, [206, 240, 75]],
  [0.62, [122, 140, 40]],
  [1, [6, 3, 15]],
];
const BAND = 34; // characters in the transition band
const START = 0.06; // scroll progress where the head starts / reaches the last character
const END = 0.72;

const clamp = (v: number) => Math.max(0, Math.min(1, v));
const tint = (t: number) => {
  const x = clamp(t);
  let k = 1;
  while (k < STOPS.length - 1 && x > STOPS[k][0]) k++;
  const [t0, c0] = STOPS[k - 1];
  const [t1, c1] = STOPS[k];
  const u = (x - t0) / (t1 - t0 || 1);
  return `rgb(${c0.map((v, j) => Math.round(v + (c1[j] - v) * u)).join(',')})`;
};

/**
 * The testimonial's scroll scrub (UX Design prototype): the section is a tall track with a sticky
 * frame; while it is pinned, a reading head moves through the quote and colours it character by
 * character, the marks and the author fade in at the start. The server renders the plain quote
 * in its final colour (no JS / reduced motion = a static quote); once near the viewport the
 * quote is split into character spans by React (never `innerHTML`: CMS copy) and coloured on
 * scroll with direct style writes, one rAF per frame, nothing while off screen.
 */
export const QuoteScrub = ({
  quote,
  className,
  stickyClassName,
  quoteClassName,
  openMark,
  closeMark,
  author,
}: Props) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(rootRef);
  const [isScrubbing, setIsScrubbing] = useState(false);

  useEffect(() => {
    if (!isInView || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    setIsScrubbing(true);
  }, [isInView]);

  useEffect(() => {
    const root = rootRef.current;
    if (!isScrubbing || !root) return;

    const chars = Array.from(root.querySelectorAll<HTMLElement>('[data-c]'));
    const colors: string[] = [];
    let raf = 0;

    const update = () => {
      raf = 0;
      const rect = root.getBoundingClientRect();
      if (rect.bottom < -200 || rect.top > window.innerHeight + 200) return;
      const range = root.offsetHeight - window.innerHeight;
      const progress = clamp(-rect.top / (range || 1));
      root.style.setProperty('--o', String(clamp(progress / 0.08)));
      const head = clamp((progress - START) / (END - START)) * (chars.length + BAND);
      chars.forEach((char, i) => {
        const color = tint((head - i) / BAND);
        if (colors[i] !== color) {
          colors[i] = color;
          char.style.color = color;
        }
      });
    };
    const request = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', request, { passive: true });
    window.addEventListener('resize', request);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', request);
      window.removeEventListener('resize', request);
    };
  }, [isScrubbing]);

  // Words stay unbroken (nowrap), characters are the coloured units.
  const words = quote.split(' ');

  return (
    <div
      ref={rootRef}
      className={className}
      data-scrub={isScrubbing ? '' : undefined}
      style={isScrubbing ? ({ '--o': 0 } as CSSProperties) : undefined}
    >
      <figure className={stickyClassName}>
        {openMark}
        <blockquote className={quoteClassName}>
          <p>
            {isScrubbing
              ? words.map((word, w) => (
                  <span key={w}>
                    {w > 0 && ' '}
                    <span style={{ whiteSpace: 'nowrap' }}>
                      {Array.from(word).map((ch, c) => (
                        <span key={c} data-c="" style={{ color: 'rgb(214,213,219)' }}>
                          {ch}
                        </span>
                      ))}
                    </span>
                  </span>
                ))
              : quote}
          </p>
        </blockquote>
        {closeMark}
        {author}
      </figure>
    </div>
  );
};
